import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

type FormData = {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  level?: string;
  consent: boolean;
};

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const schema = useMemo(() => z.object({
    email: z.string().email(t('auth.invalidEmail')),
    password: z
      .string()
      .min(8, t('auth.minChars'))
      .regex(/[A-Z]/, t('auth.needUppercase'))
      .regex(/[a-z]/, t('auth.needLowercase'))
      .regex(/[0-9]/, t('auth.needNumber')),
    first_name: z.string().min(2, t('auth.firstNameRequired')),
    last_name: z.string().min(2, t('auth.lastNameRequired')),
    phone: z.string().optional(),
    level: z.string().optional(),
    consent: z.literal(true, { errorMap: () => ({ message: t('auth.consentRequired') }) }),
  }), [t]);

  const LEVEL_OPTIONS = [
    { value: '', label: t('auth.levelSelect') },
    { value: 'Anfänger', label: t('auth.levelBeginner') },
    { value: 'Fortgeschrittener', label: t('auth.levelAdvanced') },
    { value: 'Erfahren', label: t('auth.levelExperienced') },
    { value: 'Wettkampf', label: t('auth.levelCompetition') },
  ];

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  async function onSubmit(data: FormData) {
    setError('');
    try {
      await apiClient.post('/auth/register', {
        ...data,
        level: data.level || undefined,
        phone: data.phone || undefined,
      });
      await login(data.email, data.password);
      navigate('/dashboard');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('auth.registerError'));
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.registerTitle')}</h1>
          <p className="text-gray-600 mt-2">{t('auth.registerWelcome')}</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">{t('auth.firstName')}</label>
                <input
                  type="text"
                  autoComplete="given-name"
                  {...register('first_name')}
                  className={`input ${errors.first_name ? 'input-error' : ''}`}
                  placeholder="Max"
                />
                {errors.first_name && <p className="error-text">{errors.first_name.message}</p>}
              </div>
              <div>
                <label className="label">{t('auth.lastName')}</label>
                <input
                  type="text"
                  autoComplete="family-name"
                  {...register('last_name')}
                  className={`input ${errors.last_name ? 'input-error' : ''}`}
                  placeholder="Mustermann"
                />
                {errors.last_name && <p className="error-text">{errors.last_name.message}</p>}
              </div>
            </div>

            <div>
              <label className="label">{t('auth.email')}</label>
              <input
                type="email"
                autoComplete="email"
                {...register('email')}
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder={t('auth.emailPlaceholder')}
              />
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>

            <div>
              <label className="label">{t('auth.password')}</label>
              <input
                type="password"
                autoComplete="new-password"
                {...register('password')}
                className={`input ${errors.password ? 'input-error' : ''}`}
                placeholder={t('auth.passwordPlaceholder')}
              />
              {errors.password && <p className="error-text">{errors.password.message}</p>}
            </div>

            <div>
              <label className="label">{t('auth.phone')}</label>
              <input
                type="tel"
                autoComplete="tel"
                {...register('phone')}
                className="input"
                placeholder="+49 123 456789"
              />
            </div>

            <div>
              <label className="label">{t('auth.levelOptional')}</label>
              <select {...register('level')} className="input">
                {LEVEL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">{t('auth.levelEloHint')}</p>
            </div>

            <div className="pt-1">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('consent')}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                />
                <span className="text-sm text-gray-600">
                  {t('auth.consentText')}{' '}
                  <Link to="/agb" target="_blank" className="text-primary-600 hover:text-primary-700 underline underline-offset-2">
                    {t('footer.agb')}
                  </Link>
                  {' '}{t('auth.consentAnd')}{' '}
                  <Link to="/datenschutz" target="_blank" className="text-primary-600 hover:text-primary-700 underline underline-offset-2">
                    {t('footer.datenschutz')}
                  </Link>
                  {' '}{t('auth.consentSuffix')}
                </span>
              </label>
              {errors.consent && <p className="error-text mt-1">{errors.consent.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary w-full mt-2"
            >
              {isSubmitting ? t('auth.registering') : t('auth.registerButton')}
            </button>
          </form>
        </div>

        <p className="text-center mt-6 text-sm text-gray-600">
          {t('auth.alreadyRegistered')}{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            {t('auth.loginLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
